import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Text } from '../../atoms/Text'
import {
  ResizableDetailPanel,
  DetailPanelHeader,
  DetailPanelContent,
} from '../ResizableDetailPanel'
import { TransactionDetailProps, Transaction } from './TransactionDetail.types'
import { MAPBOX_TOKEN, LIGHT_MAPSTYLE } from '../../../config/constants'
import {
  DetailHeaderInfo,
  HeaderActions,
  TransactionInfoSection,
  TransactionInfoMainContainer,
  TransactionInfoContent,
  DetailTitle,
  DetailSubtitle,
  HeaderMetrics,
  TransactionMapContainer,
  MetricItem,
  MetricLabel,
  MetricValue,
  TransactionBadge,
  DetailContent,
  DataGrid,
  DataGroup,
  DataSectionTitle,
  DataRow,
  DataLabel,
  DataValue,
  PriceValue,
  DescriptionBox,
  LoadingState,
  ErrorState,
  ParticipantsContainer,
  ParticipantGroup,
  ParticipantGroupHeader,
  ParticipantList,
  ParticipantCard,
  ParticipantType,
  ParticipantName,
  ParticipantDetails,
  PropertyCard,
  PropertyHeader,
  PropertyBadge,
  MapMarkerBadge,
  PropertyId,
  PropertyDetails,
  PropertyInfo,
  PropertyInfoRow,
  PropertyUnits,
  UnitsTitle,
  UnitItem,
  UnitType,
  UnitDetails,
  CollapsibleSection,
  CollapsibleHeader,
  CollapsibleContent,
} from './TransactionDetail.styles'

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN

export const TransactionDetail: React.FC<TransactionDetailProps> = ({
  isOpen,
  onClose,
  transactionId,
  loadTransaction,
  isSaved,
  onToggleSave,
  contained = false,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'de-CH'
  const [transaction, setTransaction] = useState<Transaction | null>(null)
  const [loading, setLoading] = useState(false)
  const [showRawData, setShowRawData] = useState(false)
  const [propertyMarkerMap, setPropertyMarkerMap] = useState<
    Map<number, number>
  >(new Map())
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)

  useEffect(() => {
    if (!transactionId || !loadTransaction) {
      setTransaction(null)
      return
    }

    const loadTransactionData = async () => {
      setLoading(true)
      try {
        const data = await loadTransaction(transactionId)
        if (data?.transaction_properties) {
          data.transaction_properties.forEach((prop, idx) => {
            console.log(`Property ${idx}:`, {
              location: prop.location,
              geo_center: prop.geo_center,
              city_name: prop.city_name,
              npa_name: prop.npa_name,
            })
          })
        }
        setTransaction(data)
      } catch (error) {
        console.error('Error loading transaction:', error)
        setTransaction(null)
      } finally {
        setLoading(false)
      }
    }

    loadTransactionData()
  }, [transactionId, loadTransaction])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  // Initialize map for properties with locations
  useEffect(() => {
    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      console.log('Map initialization starting...')
      console.log('Map container ref:', mapContainerRef.current)
      console.log(
        'Transaction properties:',
        transaction?.transaction_properties
      )

      // Add a visible indicator that code is running
      if (mapContainerRef.current) {
        mapContainerRef.current.style.background = '#f0f0f0'
        mapContainerRef.current.innerHTML = `<div style="padding: 20px; text-align: center;">${t('transactions.detailPanel.initializingMap')}</div>`
      }

      if (
        !mapContainerRef.current ||
        !transaction?.transaction_properties ||
        transaction.transaction_properties.length === 0
      ) {
        console.log('Early return - no container or properties')
        return
      }

      // Clear any existing content
      if (mapContainerRef.current) {
        mapContainerRef.current.innerHTML = ''
      }

      if (!MAPBOX_TOKEN) {
        console.warn('Mapbox access token not configured')
        // Show message in container
        if (mapContainerRef.current) {
          mapContainerRef.current.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #888; font-size: 12px;">${t('transactions.detailPanel.mapTokenNotConfigured')}</div>`
        }
        return
      }

      // Extract and group coordinates from properties
      const propertyGroups = new Map<
        string,
        Array<{ property: any; index: number }>
      >()
      const coordinatesMap = new Map<string, [number, number]>()

      // Iterate through transaction properties and extract coordinates from land_geo_center
      if (transaction.transaction_properties) {
        transaction.transaction_properties.forEach((property, index) => {
          console.log(`Processing property ${index + 1}:`, property)

          // Check for land_geo_center string field (format: "lon,lat" or "POINT(lon lat)")
          if (property.land_geo_center) {
            console.log('Found land_geo_center:', property.land_geo_center)
            let lon: number | null = null
            let lat: number | null = null

            // Try comma-separated format first (as shown in the example)
            if (property.land_geo_center.includes(',')) {
              const parts = property.land_geo_center
                .split(',')
                .map((s) => s.trim())
              if (parts.length === 2) {
                lon = parseFloat(parts[0])
                lat = parseFloat(parts[1])
              }
            } else {
              // Try POINT format as fallback
              const match = property.land_geo_center.match(
                /POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i
              )
              if (match) {
                lon = parseFloat(match[1])
                lat = parseFloat(match[2])
              }
            }

            if (lon !== null && lat !== null && !isNaN(lon) && !isNaN(lat)) {
              console.log(`Property ${index + 1} coordinates:`, { lon, lat })
              const coordKey = `${lon},${lat}`

              if (!propertyGroups.has(coordKey)) {
                propertyGroups.set(coordKey, [])
                coordinatesMap.set(coordKey, [lon, lat])
              }
              propertyGroups.get(coordKey)!.push({ property, index })
            }
          }
        })
      }

      // Convert groups to array for easier iteration
      const markerGroups = Array.from(propertyGroups.entries()).map(
        ([coordKey, properties], groupIndex) => ({
          coord: coordinatesMap.get(coordKey)!,
          properties,
          markerNumber: groupIndex + 1,
        })
      )

      // Build property to marker number mapping
      const newPropertyMarkerMap = new Map<number, number>()
      markerGroups.forEach(({ properties, markerNumber }) => {
        properties.forEach(({ index }) => {
          newPropertyMarkerMap.set(index, markerNumber)
        })
      })
      setPropertyMarkerMap(newPropertyMarkerMap)

      console.log('Marker groups found:', markerGroups)

      // If no coordinates found, use a default center based on location names or Switzerland center
      let mapCenter: [number, number] = [8.2275, 46.8182] // Switzerland center
      let zoom = 8

      if (markerGroups.length > 0) {
        mapCenter = markerGroups[0].coord
        zoom = markerGroups.length === 1 ? 14 : 12
      } else {
        console.log('No coordinates found, using default Switzerland center')
        // You could potentially geocode the city/region names here
      }

      // Initialize map
      try {
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: LIGHT_MAPSTYLE,
          center: mapCenter,
          zoom: zoom,
          interactive: true, // Allow basic interaction
          attributionControl: false,
        })

        mapRef.current = map

        map.on('load', () => {
          console.log('Map loaded successfully')

          // Only add markers if we have coordinates
          if (markerGroups.length > 0) {
            // Add markers for each group
            markerGroups.forEach(({ coord, properties, markerNumber }) => {
              const el = document.createElement('div')
              el.className = 'property-marker'
              el.style.width = '28px'
              el.style.height = '28px'
              el.style.borderRadius = '50%'
              el.style.backgroundColor = '#2563eb'
              el.style.border = '3px solid white'
              el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'
              el.style.display = 'flex'
              el.style.alignItems = 'center'
              el.style.justifyContent = 'center'
              el.style.color = 'white'
              el.style.fontSize = '12px'
              el.style.fontWeight = 'bold'
              el.style.cursor = 'pointer'
              el.innerText = markerNumber.toString()

              // Create popup with all properties info at this location
              const propertiesInfo = properties
                .map(
                  ({ property, index }) => `
            <div style="border-top: ${index > 0 ? '1px solid #eee' : 'none'}; padding-top: ${index > 0 ? '4px' : '0'}; margin-top: ${index > 0 ? '4px' : '0'};">
              <strong>${t('transactions.detailPanel.mapPopup.propertyNumber', { index: index + 1 })}</strong><br/>
              ${property.city_name || ''} ${property.npa_name || ''}<br/>
              ${property.land_area ? t('transactions.detailPanel.mapPopup.land', { area: property.land_area }) : ''}
              ${property.surface_area_total ? `<br/>${t('transactions.detailPanel.mapPopup.surface', { area: property.surface_area_total })}` : ''}
            </div>
          `
                )
                .join('')

              const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
              <div style="padding: 8px; font-size: 12px;">
                ${properties.length > 1 ? `<strong>${t('transactions.detailPanel.mapPopup.propertiesAtLocation', { count: properties.length })}</strong><br/><br/>` : ''}
                ${propertiesInfo}
              </div>
            `)

              new mapboxgl.Marker(el)
                .setLngLat(coord)
                .setPopup(popup)
                .addTo(map)
            })

            // Fit map to show all markers
            if (markerGroups.length > 1) {
              const bounds = new mapboxgl.LngLatBounds()
              markerGroups.forEach(({ coord }) => {
                bounds.extend(coord)
              })
              map.fitBounds(bounds, { padding: 50 })
            }
          } else {
            // Show a message that no exact locations are available
            const messageEl = document.createElement('div')
            messageEl.style.position = 'absolute'
            messageEl.style.top = '10px'
            messageEl.style.left = '10px'
            messageEl.style.padding = '8px 12px'
            messageEl.style.background = 'rgba(255, 255, 255, 0.9)'
            messageEl.style.borderRadius = '4px'
            messageEl.style.fontSize = '12px'
            messageEl.style.color = '#666'
            messageEl.innerText = t(
              'transactions.detailPanel.locationsNotAvailable'
            )
            mapContainerRef.current?.appendChild(messageEl)
          }
        })

        map.on('error', (error) => {
          console.error('Map error:', error)
        })
      } catch (error) {
        console.error('Failed to initialize map:', error)
      }
    }, 100) // Small delay to ensure DOM is ready

    return () => {
      clearTimeout(timer)
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [transaction?.transaction_properties])

  // Early return for closed state
  if (!isOpen) return null

  const formatPrice = (price?: number) => {
    if (!price) return '—'
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const formatArea = (area?: number) => {
    if (!area) return '—'
    return `${new Intl.NumberFormat(locale).format(area)} m²`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return '—'
    }
  }

  const getParticipantTypeLabel = (type?: string) => {
    const typeKeyMap: Record<string, string> = {
      individual: 'individual',
      company: 'company',
      estate: 'estate',
      non_profit: 'nonProfit',
      public_body: 'publicBody',
      other: 'other',
    }
    if (!type) return t('transactions.detailPanel.participantTypes.unknown')
    const key = typeKeyMap[type]
    return key ? t(`transactions.detailPanel.participantTypes.${key}`) : type
  }

  const getLegalTypeLabel = (type?: string) => {
    const typeKeyMap: Record<string, string> = {
      PPE: 'PPE',
      'B-F': 'BF',
      COP: 'COP',
      DDP: 'DDP',
      DP: 'DP',
    }
    if (!type) return type
    const key = typeKeyMap[type]
    return key ? t(`transactions.detailPanel.legalTypes.${key}`) : type
  }

  const getUnitTypeLabel = (type?: string) => {
    const typeKeyMap: Record<string, string> = {
      appartement: 'appartement',
      'Habitation à un seul logement': 'singleFamily',
      balcon: 'balcon',
      cave: 'cave',
      'Garage privé': 'privateGarage',
      loggia: 'loggia',
      box: 'box',
      garage: 'garage',
    }
    if (!type) return type
    const key = typeKeyMap[type]
    return key ? t(`transactions.detailPanel.unitTypes.${key}`) : type
  }

  // Get primary location for header
  const getPrimaryLocation = () => {
    if (!transaction?.transaction_properties?.length) return '—'
    const firstProperty = transaction.transaction_properties[0]
    const parts = []
    if (firstProperty.city_name) parts.push(firstProperty.city_name)
    if (firstProperty.npa_name) parts.push(firstProperty.npa_name)
    return parts.length > 0 ? parts.join(', ') : '—'
  }

  // Group participants by role
  const buyers =
    transaction?.transaction_participants?.filter((p) => p.role === 'buyer') ||
    []
  const sellers =
    transaction?.transaction_participants?.filter((p) => p.role === 'seller') ||
    []

  return (
    <ResizableDetailPanel
      isOpen={isOpen}
      onClose={onClose}
      storageKey="transaction_detail_panel_width"
      defaultWidth="900px"
      minWidth={600}
      maxWidthPercentage={0.8}
      contained={contained}
    >
      <DetailPanelHeader onClose={onClose}>
        <DetailHeaderInfo>
          <Text variant="caption" color="secondary">
            {t('transactions.detailPanel.type')}
          </Text>
        </DetailHeaderInfo>
        <HeaderActions>
          {onToggleSave && (
            <IconButton
              onClick={onToggleSave}
              size="small"
              variant={isSaved ? 'solid' : 'ghost'}
              title={
                isSaved
                  ? t('transactions.detailPanel.removeFromSaved')
                  : t('transactions.detailPanel.saveTransaction')
              }
              aria-label={
                isSaved
                  ? t('transactions.detailPanel.removeFromSaved')
                  : t('transactions.detailPanel.saveTransaction')
              }
            >
              <Icon name={isSaved ? 'bookmark' : 'bookmark'} size="small" />
            </IconButton>
          )}
        </HeaderActions>
      </DetailPanelHeader>

      <DetailPanelContent>
        {loading && (
          <DetailContent>
            <LoadingState>{t('transactions.detailPanel.loading')}</LoadingState>
          </DetailContent>
        )}
        {!loading && !transaction && (
          <DetailContent>
            <ErrorState>{t('transactions.detailPanel.notFound')}</ErrorState>
          </DetailContent>
        )}
        {transaction && !loading && (
          <>
            {/* Transaction Info Section - Title, Location, Metrics, Map */}
            <TransactionInfoSection>
              <TransactionInfoMainContainer>
                <TransactionInfoContent>
                  <DetailTitle>
                    <Icon name="file-text" size="small" />
                    {transaction?.official_id ||
                      t('transactions.detailPanel.transactionNumber', {
                        id: transaction?.id,
                      }) ||
                      t('transactions.detailPanel.title')}
                    {transaction?.transaction_step_name && (
                      <TransactionBadge $type={transaction.transaction_step_name}>
                        {transaction.transaction_step_name}
                      </TransactionBadge>
                    )}
                  </DetailTitle>
                  <DetailSubtitle>
                    <Icon name="map-pin" size="small" />
                    {getPrimaryLocation()}
                    <span style={{ marginLeft: '8px', color: '#888' }}>
                      {formatDate(transaction?.transaction_date)}
                    </span>
                  </DetailSubtitle>
                </TransactionInfoContent>

                {/* Map for properties - on the right side */}
                {transaction.transaction_properties &&
                  transaction.transaction_properties.length > 0 && (
                    <TransactionMapContainer ref={mapContainerRef}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          color: '#888',
                          fontSize: '12px',
                        }}
                      >
                        {t('transactions.detailPanel.loadingMap')}
                      </div>
                    </TransactionMapContainer>
                  )}
              </TransactionInfoMainContainer>

              {/* Metrics Row */}
              <HeaderMetrics>
                <MetricItem>
                  <MetricLabel>
                    {t('transactions.detailPanel.labels.totalPrice')}
                  </MetricLabel>
                  <MetricValue $highlight>
                    {formatPrice(transaction?.transaction_total_price)}
                  </MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>
                    {t('transactions.detailPanel.labels.properties')}
                  </MetricLabel>
                  <MetricValue>{transaction?.properties_count || 0}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>
                    {t('transactions.detailPanel.labels.participants')}
                  </MetricLabel>
                  <MetricValue>
                    {(transaction?.buyers_count || 0) +
                      (transaction?.sellers_count || 0)}
                  </MetricValue>
                </MetricItem>
              </HeaderMetrics>
            </TransactionInfoSection>

            <DetailContent>
              {/* Compact Transaction Information */}
              <DataGroup>
                <DataSectionTitle>
                  <Icon name="info" size="small" />
                  {t('transactions.detailPanel.sections.transactionDetails')}
                </DataSectionTitle>
                <DataGrid style={{ marginBottom: 0 }}>
                  <div>
                    <DataRow>
                      <DataLabel>
                        {t('transactions.detailPanel.labels.transactionId')}
                      </DataLabel>
                      <DataValue>{transaction.transaction_id || '—'}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>
                        {t('transactions.detailPanel.labels.officialId')}
                      </DataLabel>
                      <DataValue>{transaction.official_id || '—'}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>
                        {t('transactions.detailPanel.labels.totalPrice')}
                      </DataLabel>
                      <PriceValue>
                        {formatPrice(transaction.transaction_total_price)}
                      </PriceValue>
                    </DataRow>
                    {transaction.transaction_step_total_price && (
                      <DataRow>
                        <DataLabel>
                          {t('transactions.detailPanel.labels.stepPrice')}
                        </DataLabel>
                        <DataValue>
                          {formatPrice(
                            transaction.transaction_step_total_price
                          )}
                        </DataValue>
                      </DataRow>
                    )}
                  </div>
                  <div>
                    <DataRow>
                      <DataLabel>
                        {t('transactions.detailPanel.labels.transactionDate')}
                      </DataLabel>
                      <DataValue>
                        {formatDate(transaction.transaction_date)}
                      </DataValue>
                    </DataRow>
                    {transaction.publication_date && (
                      <DataRow>
                        <DataLabel>
                          {t('transactions.detailPanel.labels.publicationDate')}
                        </DataLabel>
                        <DataValue>
                          {formatDate(transaction.publication_date)}
                        </DataValue>
                      </DataRow>
                    )}
                    <DataRow>
                      <DataLabel>
                        {t('transactions.detailPanel.labels.language')}
                      </DataLabel>
                      <DataValue>{transaction.language || '—'}</DataValue>
                    </DataRow>
                  </div>
                </DataGrid>
              </DataGroup>

              {/* Participants Section - Side by side */}
              {(buyers.length > 0 || sellers.length > 0) && (
                <DataGroup>
                  <DataSectionTitle>
                    <Icon name="users" size="small" />
                    {t('transactions.detailPanel.sections.participants')}
                  </DataSectionTitle>
                  <ParticipantsContainer>
                    {buyers.length > 0 && (
                      <ParticipantGroup>
                        <ParticipantGroupHeader $role="buyer">
                          {t('transactions.detailPanel.labels.buyers')}
                          <span>{buyers.length}</span>
                        </ParticipantGroupHeader>
                        <ParticipantList>
                          {buyers.map((participant, index) => (
                            <ParticipantCard key={`buyer-${index}`}>
                              <ParticipantType>
                                {getParticipantTypeLabel(participant.type)}
                                {participant.legal_form &&
                                  ` • ${participant.legal_form}`}
                              </ParticipantType>
                              <ParticipantName>
                                {participant.full_name || '—'}
                              </ParticipantName>
                              <ParticipantDetails>
                                {participant.address_street && (
                                  <div>
                                    {participant.address_street}{' '}
                                    {participant.address_number}
                                    {participant.address_city &&
                                      `, ${participant.address_city}`}
                                    {participant.address_postal_code &&
                                      ` ${participant.address_postal_code}`}
                                  </div>
                                )}
                                {(participant.capacity ||
                                  participant.share_text) && (
                                  <div>
                                    {participant.capacity &&
                                      `${t('transactions.detailPanel.labels.capacity')}: ${participant.capacity}`}
                                    {participant.capacity &&
                                      participant.share_text &&
                                      ' • '}
                                    {participant.share_text &&
                                      `${t('transactions.detailPanel.labels.share')}: ${participant.share_text}`}
                                  </div>
                                )}
                              </ParticipantDetails>
                            </ParticipantCard>
                          ))}
                        </ParticipantList>
                      </ParticipantGroup>
                    )}

                    {sellers.length > 0 && (
                      <ParticipantGroup>
                        <ParticipantGroupHeader $role="seller">
                          {t('transactions.detailPanel.labels.sellers')}
                          <span>{sellers.length}</span>
                        </ParticipantGroupHeader>
                        <ParticipantList>
                          {sellers.map((participant, index) => (
                            <ParticipantCard key={`seller-${index}`}>
                              <ParticipantType>
                                {getParticipantTypeLabel(participant.type)}
                                {participant.legal_form &&
                                  ` • ${participant.legal_form}`}
                              </ParticipantType>
                              <ParticipantName>
                                {participant.full_name || '—'}
                              </ParticipantName>
                              <ParticipantDetails>
                                {participant.address_street && (
                                  <div>
                                    {participant.address_street}{' '}
                                    {participant.address_number}
                                    {participant.address_city &&
                                      `, ${participant.address_city}`}
                                    {participant.address_postal_code &&
                                      ` ${participant.address_postal_code}`}
                                  </div>
                                )}
                                {(participant.capacity ||
                                  participant.share_text) && (
                                  <div>
                                    {participant.capacity &&
                                      `${t('transactions.detailPanel.labels.capacity')}: ${participant.capacity}`}
                                    {participant.capacity &&
                                      participant.share_text &&
                                      ' • '}
                                    {participant.share_text &&
                                      `${t('transactions.detailPanel.labels.share')}: ${participant.share_text}`}
                                  </div>
                                )}
                                {participant.previous_buy_date && (
                                  <div>
                                    {t(
                                      'transactions.detailPanel.labels.previous'
                                    )}
                                    :{' '}
                                    {formatDate(participant.previous_buy_date)}
                                  </div>
                                )}
                              </ParticipantDetails>
                            </ParticipantCard>
                          ))}
                        </ParticipantList>
                      </ParticipantGroup>
                    )}
                  </ParticipantsContainer>
                </DataGroup>
              )}

              {/* Properties Section - Compact */}
              {transaction.transaction_properties &&
                transaction.transaction_properties.length > 0 && (
                  <DataGroup>
                    <DataSectionTitle>
                      <Icon name="building" size="small" />
                      {t('transactions.detailPanel.sections.propertiesCount', {
                        count: transaction.transaction_properties.length,
                      })}
                    </DataSectionTitle>
                    {transaction.transaction_properties.map(
                      (property, index) => {
                        // Get the marker number for this property (if it has coordinates)
                        const markerNumber = propertyMarkerMap.get(index)

                        return (
                          <PropertyCard key={index}>
                            <PropertyHeader>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                {markerNumber !== undefined && (
                                  <MapMarkerBadge>
                                    {markerNumber}
                                  </MapMarkerBadge>
                                )}
                                <PropertyBadge $type={property.legal_type}>
                                  {getLegalTypeLabel(property.legal_type)}
                                </PropertyBadge>
                                <PropertyId>
                                  {property.legal_id ||
                                    t(
                                      'transactions.detailPanel.labels.property',
                                      { index: index + 1 }
                                    )}
                                </PropertyId>
                              </div>
                              {property.price && (
                                <PriceValue style={{ fontSize: '13px' }}>
                                  {formatPrice(property.price)}
                                </PriceValue>
                              )}
                            </PropertyHeader>

                            <PropertyDetails>
                              <PropertyInfo>
                                <PropertyInfoRow>
                                  <span>
                                    {t(
                                      'transactions.detailPanel.labels.location'
                                    )}
                                  </span>
                                  <span>
                                    {[property.city_name, property.npa_name]
                                      .filter(Boolean)
                                      .join(', ') || '—'}
                                  </span>
                                </PropertyInfoRow>
                                <PropertyInfoRow>
                                  <span>
                                    {t(
                                      'transactions.detailPanel.labels.region'
                                    )}
                                  </span>
                                  <span>{property.region_name || '—'}</span>
                                </PropertyInfoRow>
                                {property.floor_nb && (
                                  <PropertyInfoRow>
                                    <span>
                                      {t(
                                        'transactions.detailPanel.labels.floors'
                                      )}
                                    </span>
                                    <span>{property.floor_nb}</span>
                                  </PropertyInfoRow>
                                )}
                              </PropertyInfo>
                              <PropertyInfo>
                                <PropertyInfoRow>
                                  <span>
                                    {t(
                                      'transactions.detailPanel.labels.landArea'
                                    )}
                                  </span>
                                  <span>{formatArea(property.land_area)}</span>
                                </PropertyInfoRow>
                                <PropertyInfoRow>
                                  <span>
                                    {t(
                                      'transactions.detailPanel.labels.totalSurface'
                                    )}
                                  </span>
                                  <span>
                                    {formatArea(property.surface_area_total)}
                                  </span>
                                </PropertyInfoRow>
                                {(property.property_share ||
                                  property.share_thousands) && (
                                  <PropertyInfoRow>
                                    <span>
                                      {t(
                                        'transactions.detailPanel.labels.share'
                                      )}
                                    </span>
                                    <span>
                                      {property.property_share ||
                                        property.share_thousands ||
                                        '—'}
                                    </span>
                                  </PropertyInfoRow>
                                )}
                              </PropertyInfo>
                            </PropertyDetails>

                            {/* Units - Compact display */}
                            {property.units && property.units.length > 0 && (
                              <PropertyUnits>
                                <UnitsTitle>
                                  {t(
                                    'transactions.detailPanel.labels.unitsCount',
                                    { count: property.units.length }
                                  )}
                                </UnitsTitle>
                                {property.units.map((unit, unitIndex) => (
                                  <UnitItem key={unitIndex}>
                                    <UnitType>
                                      {getUnitTypeLabel(unit.unit_type)}
                                    </UnitType>
                                    <UnitDetails>
                                      {unit.unit_plan_number && (
                                        <span>№{unit.unit_plan_number}</span>
                                      )}
                                      {unit.unit_area && (
                                        <span>
                                          {formatArea(unit.unit_area)}
                                        </span>
                                      )}
                                      {unit.unit_room_count && (
                                        <span>{unit.unit_room_count}R</span>
                                      )}
                                      {unit.unit_floor_label && (
                                        <span>F{unit.unit_floor_label}</span>
                                      )}
                                    </UnitDetails>
                                  </UnitItem>
                                ))}
                              </PropertyUnits>
                            )}
                          </PropertyCard>
                        )
                      }
                    )}
                  </DataGroup>
                )}

              {/* Transaction Text */}
              {transaction.transaction_step_text && (
                <DataGroup>
                  <DataSectionTitle>
                    <Icon name="file-text" size="small" />
                    {t(
                      'transactions.detailPanel.sections.transactionDescription'
                    )}
                  </DataSectionTitle>
                  <DescriptionBox>
                    {transaction.transaction_step_text}
                  </DescriptionBox>
                </DataGroup>
              )}

              {/* Raw Data - Collapsible */}
              {transaction.transaction_raw_data && (
                <CollapsibleSection $isOpen={showRawData}>
                  <CollapsibleHeader
                    onClick={() => setShowRawData(!showRawData)}
                  >
                    <span>
                      <Icon
                        name="code"
                        size="small"
                        style={{ marginRight: '6px' }}
                      />
                      {t(
                        'transactions.detailPanel.sections.rawTransactionData'
                      )}
                    </span>
                    <Icon
                      name={showRawData ? 'arrow-up' : 'arrow-down'}
                      size="small"
                    />
                  </CollapsibleHeader>
                  <CollapsibleContent $isOpen={showRawData}>
                    <DescriptionBox
                      style={{ fontFamily: 'monospace', fontSize: '11px' }}
                    >
                      {transaction.transaction_raw_data}
                    </DescriptionBox>
                  </CollapsibleContent>
                </CollapsibleSection>
              )}
            </DetailContent>
          </>
        )}
      </DetailPanelContent>
    </ResizableDetailPanel>
  )
}
